import { MorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { MorphemeInfoPanelContextProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._context";
import type { InfoMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import type { DataTag, QueryKey } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useState, type PropsWithChildren } from "react";
import { View } from "@-/com.npmjs.react-native";
import { useMorphemeRouteBodyReady } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.route-body";

/**
 * Publishes `MorphemeInfoPanelContext` for an Info screen. The screen is the gate: it observed the
 * info slice query and rendered `MorphemeNotFound` for `null`, so it passes the narrowed slice plus
 * that query's key, and the panel's tabs read the resolved handle's `.data`.
 */
export function MorphemePanelContent({
  children,
  infoBackCardSlice,
  infoBackCardSliceQueryKey,
}: PropsWithChildren<{
  infoBackCardSlice: InfoMorphemeSlice;
  infoBackCardSliceQueryKey: DataTag<QueryKey, InfoMorphemeSlice | null, Error>;
}>) {
  const [morphemeHrefObject] = useContextSelectors(
    MorphemePanelsContext,
    (c) => c.morphemeHrefObject,
  );
  const resolvedInfoBackCardSlice = useResolvedQuery(infoBackCardSliceQueryKey, infoBackCardSlice);
  const [isScrolled, setIsScrolled] = useState(false);
  const floatingTabBottomInset = 48 + 8 + 24;
  const onBodyReady = useMorphemeRouteBodyReady();

  return (
    <MorphemeInfoPanelContextProvider
      floatingTabBottomInset={floatingTabBottomInset}
      infoBackCardSlice={resolvedInfoBackCardSlice}
      isScrolled={isScrolled}
      morphemeHrefObject={morphemeHrefObject}
      setIsScrolled={setIsScrolled}
    >
      {infoBackCardSlice.morphemeType === "word" ? (
        <View
          className="min-h-0 flex-1"
          onLayout={(event) => {
            if (event.nativeEvent.layout.width > 0 && event.nativeEvent.layout.height > 0) {
              onBodyReady?.();
            }
          }}
        >
          {children}
        </View>
      ) : (
        children
      )}
    </MorphemeInfoPanelContextProvider>
  );
}
